import {
    changeIbanPath,
    changePaymentDayPath,
    earlyReturnPenaltyPagePath,
    earlySettlementPagePath,
    myProfilePagePath,
} from 'components/navigation/paths';
import { Contract, ContractDetails } from 'cpfr-common';
import { RequestInitialValues } from './initialValues';
import { formatCpDate } from '@cp-shared-19/common-utilities';

export interface QuickLink {
    id: QuickLinkType;
    label: string;
    path: string;
    icon: string;
}

export enum QuickLinkType {
    CHANGE_IBAN,
    CHANGE_COLLECTION_DATE,
    CHANGE_EMAIL_TELEPHONE,
    OTHER,
    BILLING_COPY_REQUEST,
    AUTHORIZATION_DEBIT,
    TAX_DOCUMENT,
    CHANGE_DURATION_MILEAGE,
    TOTAL_EARLY_SETTLEMENT,
    EARLY_RETURN_PENALTY,
}

const singleContractLinks = (contractId: string, isSMECustomer: boolean): QuickLink[] => {
    const changeEmailTelephone: QuickLink = {
        id: QuickLinkType.CHANGE_EMAIL_TELEPHONE,
        icon: 'semantic-settings',
        label: 'quick-links.email-phone-change',
        path: myProfilePagePath(),
    };
    const changeIban: QuickLink = {
        id: QuickLinkType.CHANGE_IBAN,
        icon: 'semantic-fs-payment',
        label: 'quick-links.iban-change',
        path: changeIbanPath(contractId),
    };
    const changeCollectionDate: QuickLink = {
        id: QuickLinkType.CHANGE_COLLECTION_DATE,
        icon: 'semantic-calendar',
        label: 'quick-links.change-collection',
        path: changePaymentDayPath(contractId),
    };
    const totalEarlySettlement: QuickLink = {
        id: QuickLinkType.TOTAL_EARLY_SETTLEMENT,
        icon: 'semantic-accessoires',
        label: 'quick-links.total-early-settlement',
        path: earlySettlementPagePath(contractId),
    };
    const earlyReturnPenalty: QuickLink = {
        id: QuickLinkType.EARLY_RETURN_PENALTY,
        icon: 'semantic-calculator',
        label: 'quick-links.early-return-penalty',
        path: earlyReturnPenaltyPagePath(contractId),
    };
    const authorizationDebit: QuickLink = {
        id: QuickLinkType.AUTHORIZATION_DEBIT,
        icon: 'semantic-checkmark',
        label: 'quick-links.authorization-debit',
        path: '',
    };
    const other: QuickLink = {
        id: QuickLinkType.OTHER,
        icon: 'semantic-more',
        label: 'quick-links.other',
        path: '',
    };
    const billingCopyRequest: QuickLink = {
        id: QuickLinkType.BILLING_COPY_REQUEST,
        icon: 'documents',
        label: 'quick-links.billing-copy-request',
        path: '',
    };
    const taxDocument: QuickLink = {
        id: QuickLinkType.TAX_DOCUMENT,
        icon: 'semantic-file',
        label: 'quick-links.tax-document',
        path: '',
    };
    const changeDurationMileage: QuickLink = {
        id: QuickLinkType.CHANGE_DURATION_MILEAGE,
        icon: 'semantic-edit',
        label: 'quick-links.change-duration-mileage',
        path: '',
    };

    if (isSMECustomer) {
        return [
            changeEmailTelephone,
            changeIban,
            billingCopyRequest,
            changeCollectionDate,
            authorizationDebit,
            taxDocument,
            changeDurationMileage,
            other,
        ];
    }

    return [
        changeEmailTelephone,
        changeIban,
        changeCollectionDate,
        totalEarlySettlement,
        earlyReturnPenalty,
        authorizationDebit,
        billingCopyRequest,
        other,
        taxDocument,
        changeDurationMileage,
    ];
};

const multipleContractLinks = (contractId: string, isSMECustomer: boolean): QuickLink[] => {
    const links = [
        {
            id: QuickLinkType.CHANGE_EMAIL_TELEPHONE,
            icon: 'semantic-settings',
            label: 'quick-links.email-phone-change',
            path: myProfilePagePath(),
        },
        {
            id: QuickLinkType.CHANGE_IBAN,
            icon: 'semantic-fs-payment',
            label: 'quick-links.iban-change',
            path: changeIbanPath(contractId),
        },
        {
            id: QuickLinkType.AUTHORIZATION_DEBIT,
            icon: 'semantic-checkmark',
            label: 'quick-links.authorization-debit',
            path: '',
        },
        {
            id: QuickLinkType.TAX_DOCUMENT,
            icon: 'semantic-file',
            label: 'quick-links.tax-document',
            path: '',
        },
        {
            id: QuickLinkType.CHANGE_DURATION_MILEAGE,
            icon: 'semantic-edit',
            label: 'quick-links.change-duration-mileage',
            path: '',
        },
        {
            id: QuickLinkType.OTHER,
            icon: 'semantic-more',
            label: 'quick-links.other',
            path: '',
        },
    ];

    if (isSMECustomer) {
        return links.filter(
            (link) => link.id !== QuickLinkType.CHANGE_DURATION_MILEAGE && link.id !== QuickLinkType.TAX_DOCUMENT,
        );
    }

    return links;
};

export type FullContract = Contract & ContractDetails;

export type QuickLinkConditions = Partial<Record<QuickLinkType, (contracts: FullContract[]) => boolean>>;

const getBaseLinks = (contracts: Contract[], isSMECustomer: boolean): QuickLink[] => {
    if (!contracts.length) return [];
    const contractEndDateWas6MonthAgo = contracts.find((item) =>
        formatCpDate(item.contractEndDate).add(6, 'month').toMoment().isBefore(new Date()),
    );
    if (contractEndDateWas6MonthAgo) {
        return [
            {
                id: QuickLinkType.OTHER,
                icon: 'semantic-more',
                label: 'quick-links.other',
                path: '',
            },
        ];
    }
    const contractId = contracts[0].contractId;
    return contracts.length === 1
        ? singleContractLinks(contractId, isSMECustomer)
        : multipleContractLinks(contractId, isSMECustomer);
};

export const getQuickLinks = (
    contracts: FullContract[] = [],
    conditions: Partial<Record<QuickLinkType, (contracts: FullContract[]) => boolean>>,
    isSMECustomer: boolean,
): QuickLink[] => {
    const baseLinks = getBaseLinks(contracts, isSMECustomer);
    return baseLinks.filter((link) => {
        const condition = conditions[link.id];
        return condition ? condition(contracts) : true;
    });
};

export const canSubmitForm = (values: RequestInitialValues): boolean => {
    if (values.vehicleClaim.reparable === 'Oui') {
        return false;
    }

    return true;
};
